import { features } from "../config/features";
import type { AdminClientListItem, AdminClientSetupHealth } from "../services/api";

import type { HealthState } from "./ui";

/* ===================================================================
   Setup health: the shared vocabulary behind the sub-nav dots, the
   customer-list progress bar and the admin home's "Needs attention".

   The states themselves are computed SERVER-side (one grouped sweep
   per page rather than an N+1 across every customer) and arrive on
   `setup_health` when the caller passes `include: "health"`. This
   module owns the labels and the attention feed derived from them, so
   the copy lives in one place.
   =================================================================== */

export type SectionKey =
  | "overview"
  | "users"
  | "sso"
  | "forms"
  | "sync"
  | "reporting";

/** The sections that are always in the sub-nav, in order. Reporting is a
 *  section too (and carries a health dot), but it is feature-gated, so the
 *  customer page renders it separately rather than from this list. */
export const SECTIONS: { key: SectionKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "users", label: "Users & access" },
  { key: "sso", label: "Sign-in & SSO" },
  { key: "forms", label: "Forms & approvals" },
  { key: "sync", label: "Ticket sync" },
];

/** Fallback when the caller did not ask for health (or an older API omits it):
 *  everything unknown reads as "not set up" rather than a false green.
 *  Reporting is `null` — "not applicable", which is also what the server sends
 *  when the reporting engine is switched off, so it draws no dot either way. */
export const UNKNOWN_HEALTH: AdminClientSetupHealth = {
  overview: "todo",
  users: "todo",
  sso: "todo",
  forms: "todo",
  sync: "todo",
  reporting: null,
  done: 0,
  total: SECTIONS.length,
  unavailable: [],
};

export function healthOf(
  client: { setup_health?: AdminClientSetupHealth | null } | undefined,
): AdminClientSetupHealth {
  return client?.setup_health ?? UNKNOWN_HEALTH;
}

/** A section whose query failed. `null` from the server for reporting means the
 *  engine is off, which is not the same thing: that key is simply absent here. */
export function isSectionUnavailable(
  health: AdminClientSetupHealth,
  key: SectionKey,
): boolean {
  return (health.unavailable ?? []).includes(key);
}

export interface HealthNoticeRow {
  id: string;
  name: string;
  message: string;
}

/** One customer's health messages, one by one. Falls back to the joined
 *  `health_error` string when the API does not send the list. */
function messagesOf(client: {
  health_error?: string | null;
  health_errors?: string[];
}): string[] {
  const list = (client.health_errors ?? []).map((m) => m.trim()).filter(Boolean);
  if (list.length > 0) return [...new Set(list)];
  const joined = client.health_error?.trim();
  return joined ? [joined] : [];
}

/** Split health failures into one shared banner (a message on more than one
 *  customer) and one row per customer for whatever is left that is theirs
 *  alone. Grouped message by message (#242), so a customer with a shared
 *  outage PLUS a problem of their own still counts toward the banner, and
 *  the row only names the part that is theirs. */
export function healthNotices(
  clients: {
    id: string;
    name: string;
    health_error?: string | null;
    health_errors?: string[];
  }[],
): { banner: string[]; rows: HealthNoticeRow[] } {
  const perClient = clients.map((client) => ({ client, messages: messagesOf(client) }));
  const counts = new Map<string, number>();
  for (const { messages } of perClient) {
    for (const message of messages) counts.set(message, (counts.get(message) ?? 0) + 1);
  }
  const banner = [...counts].filter(([, n]) => n > 1).map(([message]) => message);
  const shared = new Set(banner);
  const rows = perClient
    .map(({ client, messages }) => ({
      id: client.id,
      name: client.name,
      message: messages.filter((m) => !shared.has(m)).join(" "),
    }))
    .filter((row) => row.message);
  return { banner, rows };
}

/** The Overview "Finish setting up" checklist. One row per section, with the
 *  copy that says what finishing it actually buys. */
export const CHECKLIST: { key: SectionKey; title: string; blurb: string }[] = [
  {
    key: "overview",
    title: "Company details",
    blurb: "Name, Autotask link and contact details",
  },
  {
    key: "sso",
    title: "Sign-in working",
    blurb: "A domain is allow-listed and somebody has signed in",
  },
  {
    key: "users",
    title: "First person invited",
    blurb: "Somebody at the customer who can raise and track tickets",
  },
  {
    key: "forms",
    title: "Forms enabled with routing",
    blurb: "What they can request, and who approves",
  },
  {
    key: "sync",
    title: "Ticket sync configured",
    blurb: "Queue and priority defaults for new tickets",
  },
  {
    key: "reporting",
    title: "Monthly review set up",
    blurb: "Who receives the Client Service Review on the 7th",
  },
];

/** The checklist rows still outstanding for one customer, in CHECKLIST order.
 *
 *  Reporting is only part of a customer's setup while the engine is actually
 *  on: a `null` state is the server saying it is off globally, and the build
 *  flag can hide the section independently. Either way there is nowhere for the
 *  row to link to, so it is dropped rather than shown as unfinished. */
export function outstandingChecklist(
  health: AdminClientSetupHealth,
): { key: SectionKey; title: string; blurb: string }[] {
  return CHECKLIST.filter((item) => {
    if (isSectionUnavailable(health, item.key)) return false;
    const state = health[item.key];
    if (item.key === "reporting" && (!features.reporting || state == null)) {
      return false;
    }
    return state !== "ok";
  });
}

export interface AttentionItem {
  client: AdminClientListItem;
  section: SectionKey;
  state: HealthState;
  title: string;
  blurb: string;
}

/**
 * The admin home's "Needs attention" feed: every incomplete section across
 * every ACTIVE customer, phrased as the consequence rather than the field.
 * Disabled customers are excluded — nothing about them is waiting on anyone.
 */
export function attentionItems(clients: AdminClientListItem[]): AttentionItem[] {
  const out: AttentionItem[] = [];
  for (const client of clients) {
    if (!client.is_active) continue;
    const h = client.setup_health;
    if (!h) continue;

    if (!isSectionUnavailable(h, "sso") && h.sso !== "ok") {
      // The state already encodes which it is: `todo` means no domain is
      // allow-listed (fail-closed for Microsoft and Google alike), `warn`
      // means there is a way in that nobody has used yet.
      out.push({
        client,
        section: "sso",
        state: h.sso,
        title: h.sso === "todo" ? "Nobody can sign in yet" : "Sign-in not proven yet",
        blurb:
          h.sso === "todo"
            ? "No email domain is allow-listed, and an empty list lets nobody in"
            : "A domain is allow-listed but nobody has signed in, so the trust path is unproven",
      });
    }
    if (!isSectionUnavailable(h, "users") && h.users !== "ok") {
      // Only ever `todo`: the section asks for portal users, not specifically a
      // client admin, so there is no half-finished state to phrase.
      out.push({
        client,
        section: "users",
        state: h.users,
        title: "No portal users yet",
        blurb: "Nobody at the customer can sign in, so invite someone or import from Autotask",
      });
    }
    if (!isSectionUnavailable(h, "sync") && h.sync_refused) {
      // The runner skips this customer every tick, so no ticket syncs until the
      // shared Autotask id is resolved. Server text, phrased as the reason.
      out.push({
        client,
        section: "sync",
        state: "warn",
        title: "Ticket sync refused",
        blurb: `${h.sync_refused}, so no tickets sync for this customer`,
      });
    }
    if (!isSectionUnavailable(h, "forms") && h.forms === "warn") {
      out.push({
        client,
        section: "forms",
        state: "warn",
        title: "Forms enabled with no routing rule",
        blurb: "Approval requests will fall back to the iQuest service desk",
      });
    }
    if (!isSectionUnavailable(h, "forms") && h.forms === "todo") {
      out.push({
        client,
        section: "forms",
        state: "todo",
        title: "No forms enabled",
        blurb: "The Service Hub is empty for this customer",
      });
    }
  }
  return out;
}

/* ---- "recently worked on", per staff user, in localStorage ---- */

const RECENT_KEY = "iq-admin-recent-customers";
const RECENT_MAX = 5;

export function readRecent(userId: string): string[] {
  try {
    const raw = window.localStorage.getItem(`${RECENT_KEY}:${userId}`);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    // Private windows and cleared site data both land here. A missing recent
    // list is a cosmetic loss, never an error worth surfacing.
    return [];
  }
}

export function touchRecent(userId: string, companyId: string): void {
  try {
    const next = [companyId, ...readRecent(userId).filter((x) => x !== companyId)].slice(
      0,
      RECENT_MAX,
    );
    window.localStorage.setItem(`${RECENT_KEY}:${userId}`, JSON.stringify(next));
  } catch {
    // Ignore: see readRecent.
  }
}
